import React, { Component } from 'react';
import { arrayOf, shape, number, string, func } from 'prop-types';
import { first, last, get } from 'lodash';
import invariant from 'invariant';

import {
  FlexibleWidthXYPlot,
  LineSeries,
  AreaSeries,
  XAxis,
  Crosshair,
  MarkSeries,
} from 'react-vis';

import WithSubtitle from 'components/WithSubtitle';
import styles from './Chart.scss';

export default class Chart extends Component {
  state = {
    crosshairIndex: null,
  };

  render() {
    const { crosshairIndex } = this.state;

    const {
      title,
      reportedValues,
      expectedValues,
      calculateCrosshairA,
      calculateCrosshairB,
    } = this.props;

    const showExpectedValues = expectedValues && expectedValues.length > 0;
    invariant(reportedValues.length > 0, 'Chart needs at least 1 reported value');

    const mappedReportedValues = reportedValues.map(({ timestamp, value }) => ({
      x: new Date(timestamp),
      y: value,
    }));

    const mappedExpectedValues = showExpectedValues
      ? expectedValues.map(({ timestamp, value }) => ({
          x: new Date(timestamp),
          y: value,
        }))
      : [];

    const tickMargin = Math.floor(reportedValues.length / 10);

    // Increase the domain size by this amount to create a bit of padding
    const yDomainPadding = 1.5;

    // Define tick values per render so we can show the current highlighted line based on the nearest mouse X value
    const startDate = first(mappedReportedValues).x;
    const endDate = last(mappedReportedValues).x;

    const tickValues = [];
    let crosshairValuesReported = null;
    let crosshairValuesExpected = null;
    let crosshairA = null;
    let crosshairB = null;
    const showCrosshair = crosshairIndex !== null;

    // if crosshair is overlapping with start, don't render start
    if (!showCrosshair || crosshairIndex > tickMargin) {
      tickValues.push(startDate);
    }

    if (showCrosshair) {
      crosshairValuesReported = get(mappedReportedValues, crosshairIndex);
      crosshairValuesExpected = showExpectedValues
        ? get(mappedExpectedValues, crosshairIndex)
        : null;

      if (crosshairIndex > 0 && crosshairIndex < mappedReportedValues.length - 1) {
        tickValues.push(crosshairValuesReported.x);
      }

      crosshairA = calculateCrosshairA({
        reported: crosshairValuesReported.y,
        expected: crosshairValuesExpected && crosshairValuesExpected.y,
      });
      crosshairB =
        calculateCrosshairB && // b is optional
        calculateCrosshairB({
          reported: crosshairValuesReported.y,
          expected: crosshairValuesExpected && crosshairValuesExpected.y,
        });
    }

    // if crosshair is overlapping with end, don't render end
    if (!showCrosshair || crosshairIndex <= mappedReportedValues.length - tickMargin) {
      tickValues.push(endDate);
    }

    const maxValueReported = reportedValues.reduce((acc, { value }) => {
      if (acc < value) return value;
      return acc;
    }, 0);

    const maxValueExpected = expectedValues
      ? expectedValues.reduce((acc, { value }) => {
          if (acc < value) return value;
          return acc;
        }, 0)
      : 0;

    const maxValue = Math.max(maxValueReported, maxValueExpected);

    return (
      <div className={styles.main}>
        <h3 className={styles.title}>
          {title}
        </h3>
        <FlexibleWidthXYPlot
          xType="time"
          xDomain={[startDate, endDate]}
          yDomain={[0, maxValue * yDomainPadding]}
          onMouseLeave={() => this.setState({ crosshairIndex: null })}
          height={160}
          margin={{
            left: 16,
            right: 16,
            top: 0,
            bottom: 32,
          }}
        >
          <XAxis
            tickValues={tickValues}
            style={{
              text: {
                stroke: 'none',
                fill: 'rgb(0, 0, 0)',
                fontWeight: 600,
              },
            }}
          />
          {showExpectedValues &&
            <LineSeries
              data={mappedExpectedValues}
              strokeWidth={2}
              color="rgb(135, 145, 170)"
              strokeDasharray="2 2"
            />}
          <AreaSeries data={mappedReportedValues} stroke="none" fill="rgba(0, 0, 255, 0.05)" />
          <LineSeries
            data={mappedReportedValues}
            color="rgb(0, 0, 255)"
            strokeWidth={2}
            onNearestX={(value, { index }) => {
              this.setState({
                crosshairIndex: index,
              });
            }}
          />

          {showCrosshair && showExpectedValues
            ? <Crosshair values={[crosshairValuesReported, crosshairValuesExpected]}>
                <div className={styles.crosshair}>
                  {crosshairA &&
                    <WithSubtitle reversed title={crosshairA.title} className={styles.value}>
                      {crosshairA.value}
                    </WithSubtitle>}
                  {crosshairB &&
                    <WithSubtitle reversed title={crosshairB.title} className={styles.value}>
                      {crosshairB.value}
                    </WithSubtitle>}
                </div>
              </Crosshair>
            : <Crosshair values={[crosshairValuesReported]}>
                <div className={styles.crosshair}>
                  {crosshairA &&
                    <WithSubtitle reversed title={crosshairA.title} className={styles.value}>
                      {crosshairA.value}
                    </WithSubtitle>}
                  {crosshairB &&
                    <WithSubtitle reversed title={crosshairB.title} className={styles.value}>
                      {crosshairB.value}
                    </WithSubtitle>}
                </div>
              </Crosshair>}

          {showCrosshair &&
            <MarkSeries
              color="rgba(0, 0, 255, 0.8)"
              stroke="white"
              strokeWidth={2}
              size={5}
              data={[crosshairValuesReported]}
            />}
        </FlexibleWidthXYPlot>
      </div>
    );
  }
}
Chart.propTypes = {
  title: string,
  reportedValues: arrayOf(
    shape({
      timestamp: string.isRequired,
      value: number.isRequired,
    }).isRequired
  ),
  expectedValues: arrayOf(
    shape({
      timestamp: string.isRequired,
      value: number.isRequired,
    })
  ),
  calculateCrosshairA: func.isRequired,
  calculateCrosshairB: func,
};



// WEBPACK FOOTER //
// ./src/components/DataPanel/Details/Chart/index.js